/**
 * Allowed state transitions as data (§9, §12, §85 + error paths).
 *
 * Domain services MUST validate every status change against these tables AND persist it
 * via compare-and-set (`UPDATE … WHERE id = ? AND status = ?`, `meta.changes === 1`).
 * The tables are the single source of truth for the state machine SPEC (04, 05).
 */
import {
  ActivationCodeStatus,
  BatchStatus,
  DocumentStatus,
  IntegrationJobStatus,
  RegistrationStatus,
} from './enums';

export type TransitionTable<S extends string> = Readonly<Record<S, readonly S[]>>;

/**
 * Activation code (§85).
 *
 * Guards that are not expressible as pure from→to pairs (enforced by domain + SQL WHERE):
 * - `PENDING_EMAIL → AVAILABLE` only by expiry of the pending registration (§18, §72)
 *   and only while that registration is still PENDING_EMAIL (email not verified).
 * - `PENDING_EMAIL → DISABLED` cancels the pending registration in the same `db.batch()`
 *   (registration → CANCELLED, verification tokens invalidated). See CODE_DISABLE_CANCELS_PENDING.
 * - `DISABLED → AVAILABLE` only if the code was never claimed/redeemed (`claimed_at IS NULL`,
 *   `redeemed_at IS NULL`), was not replaced (`replaced_by_code_id IS NULL`) and its batch
 *   is not DISABLED.
 * - `PROVISIONING → PROVISIONING` is the retry self-loop (§85): the code stays PROVISIONING
 *   while the registration goes PROVISIONING_FAILED → PROVISIONING.
 * - CLAIMED, PROVISIONING and REDEEMED codes can never become AVAILABLE again (§21, §26).
 */
export const CODE_TRANSITIONS: TransitionTable<ActivationCodeStatus> = {
  [ActivationCodeStatus.AVAILABLE]: [
    ActivationCodeStatus.PENDING_EMAIL,
    ActivationCodeStatus.DISABLED,
  ],
  [ActivationCodeStatus.PENDING_EMAIL]: [
    ActivationCodeStatus.AVAILABLE,
    ActivationCodeStatus.CLAIMED,
    ActivationCodeStatus.DISABLED,
  ],
  [ActivationCodeStatus.CLAIMED]: [ActivationCodeStatus.PROVISIONING],
  [ActivationCodeStatus.PROVISIONING]: [
    ActivationCodeStatus.REDEEMED,
    ActivationCodeStatus.PROVISIONING,
  ],
  [ActivationCodeStatus.REDEEMED]: [],
  [ActivationCodeStatus.DISABLED]: [ActivationCodeStatus.AVAILABLE],
};

/**
 * Registration (§12, §18, §21, §22, §26, §98).
 *
 * - `PENDING_EMAIL → EMAIL_VERIFIED` by successful verification (token CAS `used_at IS NULL`).
 * - `PENDING_EMAIL → EXPIRED` by the pending cleanup cron (§72).
 * - `PENDING_EMAIL → CANCELLED` when the code or its batch is disabled while pending.
 * - `PROVISIONING → PROVISIONING` = retryable failure, attempt counter incremented.
 * - `PROVISIONING → PROVISIONING_FAILED` = permanent failure or attempts exhausted (§26).
 * - `EMAIL_VERIFIED → PROVISIONING_FAILED` = the provisioning job failed for good (DLQ, attempts
 *   exhausted) before its first attempt started; the code stays CLAIMED (CR-WS7-2).
 * - `PROVISIONING_FAILED → PROVISIONING` = admin retry (§98) or reconciliation.
 */
export const REGISTRATION_TRANSITIONS: TransitionTable<RegistrationStatus> = {
  [RegistrationStatus.PENDING_EMAIL]: [
    RegistrationStatus.EMAIL_VERIFIED,
    RegistrationStatus.EXPIRED,
    RegistrationStatus.CANCELLED,
  ],
  [RegistrationStatus.EMAIL_VERIFIED]: [
    RegistrationStatus.PROVISIONING,
    RegistrationStatus.PROVISIONING_FAILED, // job failed for good before its first attempt (DLQ)
  ],
  [RegistrationStatus.PROVISIONING]: [
    RegistrationStatus.ACTIVE,
    RegistrationStatus.PROVISIONING_FAILED,
    RegistrationStatus.PROVISIONING,
  ],
  [RegistrationStatus.PROVISIONING_FAILED]: [RegistrationStatus.PROVISIONING],
  [RegistrationStatus.ACTIVE]: [],
  [RegistrationStatus.EXPIRED]: [],
  [RegistrationStatus.CANCELLED]: [],
};

/**
 * Batch: codes are generated asynchronously (GENERATING) and the batch becomes READY when the
 * last chunk is persisted. Only READY batches can be disabled (§99); disabling is final in v1.
 * Codes of a GENERATING batch are invisible to the public API and to exports.
 */
export const BATCH_TRANSITIONS: TransitionTable<BatchStatus> = {
  [BatchStatus.GENERATING]: [BatchStatus.READY],
  [BatchStatus.READY]: [BatchStatus.DISABLED],
  [BatchStatus.DISABLED]: [],
};

/**
 * Integration job / outbox (ADR 016).
 *
 * - PENDING → DISPATCHED after a successful `queue.send`.
 * - PENDING → RUNNING: the consumer received the message before the dispatch CAS
 *   `PENDING → DISPATCHED` was written (or after that CAS failed, or after a cron re-dispatch of a
 *   PENDING job). The late dispatch CAS then matches 0 rows and reports `ALREADY_DISPATCHED`
 *   (CR-WS7-1).
 * - DISPATCHED → PENDING when the cron re-dispatches a job whose message got lost.
 * - PENDING | DISPATCHED | FAILED_RETRYABLE → RUNNING: consumer claims the job (CAS).
 * - RUNNING → FAILED_RETRYABLE: retryable error, `next_attempt_at` set, message retried with delay.
 * - RUNNING → PENDING: a crashed/timed-out run is reset by the reconciliation cron.
 * - * → DEAD: permanent error or attempts exhausted.
 * - DEAD | FAILED_RETRYABLE → PENDING: manual admin retry (`POST /admin/jobs/:id/retry`).
 */
export const JOB_TRANSITIONS: TransitionTable<IntegrationJobStatus> = {
  [IntegrationJobStatus.PENDING]: [
    IntegrationJobStatus.DISPATCHED,
    IntegrationJobStatus.RUNNING, // consumer faster than the dispatch CAS (ADR 016, CR-WS7-1)
    IntegrationJobStatus.DEAD,
  ],
  [IntegrationJobStatus.DISPATCHED]: [
    IntegrationJobStatus.RUNNING,
    IntegrationJobStatus.PENDING,
    IntegrationJobStatus.DEAD,
  ],
  [IntegrationJobStatus.RUNNING]: [
    IntegrationJobStatus.SUCCEEDED,
    IntegrationJobStatus.FAILED_RETRYABLE,
    IntegrationJobStatus.DEAD,
    IntegrationJobStatus.PENDING,
  ],
  [IntegrationJobStatus.FAILED_RETRYABLE]: [
    IntegrationJobStatus.RUNNING,
    IntegrationJobStatus.PENDING,
    IntegrationJobStatus.DEAD,
  ],
  [IntegrationJobStatus.SUCCEEDED]: [],
  [IntegrationJobStatus.DEAD]: [IntegrationJobStatus.PENDING],
};

/** Generated documents: a FAILED document can be requested again (→ PENDING). */
export const DOCUMENT_TRANSITIONS: TransitionTable<DocumentStatus> = {
  [DocumentStatus.PENDING]: [DocumentStatus.READY, DocumentStatus.FAILED],
  [DocumentStatus.READY]: [],
  [DocumentStatus.FAILED]: [DocumentStatus.PENDING],
};

export function canTransition<S extends string>(
  table: TransitionTable<S>,
  from: S,
  to: S,
): boolean {
  return table[from].includes(to);
}

export const canTransitionCode = (from: ActivationCodeStatus, to: ActivationCodeStatus): boolean =>
  canTransition(CODE_TRANSITIONS, from, to);

export const canTransitionRegistration = (
  from: RegistrationStatus,
  to: RegistrationStatus,
): boolean => canTransition(REGISTRATION_TRANSITIONS, from, to);

/**
 * Registration statuses that hold the seat of their code. At most ONE registration per code may
 * be in one of these statuses (enforced by the partial unique index
 * `registrations_one_seat_holder_per_code`). EXPIRED and CANCELLED release the code.
 */
export const SEAT_HOLDING_REGISTRATION_STATUSES = [
  RegistrationStatus.PENDING_EMAIL,
  RegistrationStatus.EMAIL_VERIFIED,
  RegistrationStatus.PROVISIONING,
  RegistrationStatus.PROVISIONING_FAILED,
  RegistrationStatus.ACTIVE,
] as const satisfies readonly RegistrationStatus[];

/** Registration statuses without outgoing transitions. */
export const TERMINAL_REGISTRATION_STATUSES = [
  RegistrationStatus.ACTIVE,
  RegistrationStatus.EXPIRED,
  RegistrationStatus.CANCELLED,
] as const satisfies readonly RegistrationStatus[];

/** Code statuses that count as „nicht eingelöst“ and are blocked by batch disable (§99). */
export const BATCH_DISABLE_AFFECTED_CODE_STATUSES = [
  ActivationCodeStatus.AVAILABLE,
  ActivationCodeStatus.PENDING_EMAIL,
] as const satisfies readonly ActivationCodeStatus[];

/** Code statuses after email verification – the seat belongs to the participant (§21). */
export const CLAIMED_CODE_STATUSES = [
  ActivationCodeStatus.CLAIMED,
  ActivationCodeStatus.PROVISIONING,
  ActivationCodeStatus.REDEEMED,
] as const satisfies readonly ActivationCodeStatus[];

/**
 * Job statuses the consumer may pick up (CAS to RUNNING). PENDING is included because the message
 * can arrive before the dispatch CAS `PENDING → DISPATCHED` (ADR 016 point 4, CR-WS7-1).
 */
export const CLAIMABLE_JOB_STATUSES = [
  IntegrationJobStatus.PENDING,
  IntegrationJobStatus.DISPATCHED,
  IntegrationJobStatus.FAILED_RETRYABLE,
] as const satisfies readonly IntegrationJobStatus[];

/**
 * Coupled transitions: which code and registration changes belong to one atomic `db.batch()`.
 * `null` = entity is not changed by this step. Used by the domain (WS1) and SPEC 04/05/27.
 */
export const COUPLED_TRANSITIONS = {
  /** §15: new registration PENDING_EMAIL + code AVAILABLE → PENDING_EMAIL. */
  START_REGISTRATION: {
    code: { from: [ActivationCodeStatus.AVAILABLE], to: ActivationCodeStatus.PENDING_EMAIL },
    registration: { from: [], to: RegistrationStatus.PENDING_EMAIL },
  },
  /** §21: verification. */
  VERIFY_EMAIL: {
    code: { from: [ActivationCodeStatus.PENDING_EMAIL], to: ActivationCodeStatus.CLAIMED },
    registration: {
      from: [RegistrationStatus.PENDING_EMAIL],
      to: RegistrationStatus.EMAIL_VERIFIED,
    },
  },
  /** §22: a provisioning attempt starts (first attempt, retry, admin retry). */
  START_PROVISIONING: {
    code: {
      from: [ActivationCodeStatus.CLAIMED, ActivationCodeStatus.PROVISIONING],
      to: ActivationCodeStatus.PROVISIONING,
    },
    registration: {
      from: [
        RegistrationStatus.EMAIL_VERIFIED,
        RegistrationStatus.PROVISIONING,
        RegistrationStatus.PROVISIONING_FAILED,
      ],
      to: RegistrationStatus.PROVISIONING,
    },
  },
  /** §32: success. */
  COMPLETE_PROVISIONING: {
    code: { from: [ActivationCodeStatus.PROVISIONING], to: ActivationCodeStatus.REDEEMED },
    registration: { from: [RegistrationStatus.PROVISIONING], to: RegistrationStatus.ACTIVE },
  },
  /**
   * §26: permanent failure – the code is not touched (stays PROVISIONING, or CLAIMED when the job
   * failed before its first attempt, CR-WS7-2) and is never released.
   */
  FAIL_PROVISIONING: {
    code: null,
    registration: {
      from: [RegistrationStatus.EMAIL_VERIFIED, RegistrationStatus.PROVISIONING],
      to: RegistrationStatus.PROVISIONING_FAILED,
    },
  },
  /** §18/§72: pending expiry. */
  EXPIRE_PENDING: {
    code: { from: [ActivationCodeStatus.PENDING_EMAIL], to: ActivationCodeStatus.AVAILABLE },
    registration: { from: [RegistrationStatus.PENDING_EMAIL], to: RegistrationStatus.EXPIRED },
  },
  /**
   * Decision (WS0): disabling a PENDING_EMAIL code (single code or whole batch) cancels the
   * pending registration. The participant has not verified yet, so the seat is not theirs.
   */
  CODE_DISABLE_CANCELS_PENDING: {
    code: { from: [ActivationCodeStatus.PENDING_EMAIL], to: ActivationCodeStatus.DISABLED },
    registration: {
      from: [RegistrationStatus.PENDING_EMAIL],
      to: RegistrationStatus.CANCELLED,
    },
  },
} as const;
export type CoupledTransition = keyof typeof COUPLED_TRANSITIONS;
